import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { setCurrentStudio } from '~entities/studios';
import { useGetStudioFromLocationId } from '~entities/studios/api/get-studio-from-location-id';
import { getToken, useTokenStore } from '~entities/studios/model/token-store';
import { useCurrentUser, useUserStore } from '~entities/users/model/user-store';
import { completeStandaloneStudioLogin } from '~shared/utils/complete-standalone-studio-login';
import { isStandaloneDevEnvironment } from '~shared/utils/is-standalone-dev-environment';
import { buildPostLoginDestination } from '~shared/utils/studio-login-redirect';

const useAuthStoresHydrated = () => {
  const [hydrated, setHydrated] = useState(
    () =>
      useUserStore.persist.hasHydrated() && useTokenStore.persist.hasHydrated(),
  );

  useEffect(() => {
    const markHydrated = () => {
      if (
        useUserStore.persist.hasHydrated() &&
        useTokenStore.persist.hasHydrated()
      ) {
        setHydrated(true);
      }
    };

    markHydrated();
    const unsubUser = useUserStore.persist.onFinishHydration(markHydrated);
    const unsubToken = useTokenStore.persist.onFinishHydration(markHydrated);
    return () => {
      unsubUser();
      unsubToken();
    };
  }, []);

  return hydrated;
};

const Component = () => {
  const navigate = Route.useNavigate();
  const { locationId, redirect } = Route.useSearch();
  const isStandaloneDev = isStandaloneDevEnvironment();
  const authHydrated = useAuthStoresHydrated();

  const { data, isError, isFetching } = useGetStudioFromLocationId(locationId, {
    // Never hit GHL token refresh outside GHL; that call hangs on studio-dev.
    enabled: !isStandaloneDev,
  });

  const currentUser = useCurrentUser();
  const [userInvalid, setUserInvalid] = useState(false);
  const [standaloneError, setStandaloneError] = useState(false);
  const standaloneStartedRef = useRef(false);

  useEffect(() => {
    if (!isStandaloneDev || !authHydrated || standaloneStartedRef.current) {
      return;
    }
    standaloneStartedRef.current = true;

    void (async () => {
      if (!getToken() || !currentUser) {
        void navigate({
          to: '/login',
          search: { id: locationId, redirect },
        });
        return;
      }

      try {
        const destination = await completeStandaloneStudioLogin(
          locationId,
          redirect,
        );
        void navigate(destination);
      } catch {
        setStandaloneError(true);
      }
    })();
  }, [
    authHydrated,
    currentUser,
    isStandaloneDev,
    locationId,
    navigate,
    redirect,
  ]);

  useEffect(() => {
    if (isStandaloneDev) {
      return;
    }

    if (!currentUser) {
      setUserInvalid(true);
      return;
    }

    if (!data?.studio) {
      return;
    }

    setCurrentStudio(data.studio);
    void navigate(
      buildPostLoginDestination(data.studio.locationId, redirect),
    );
  }, [currentUser, data, isStandaloneDev, navigate, redirect]);

  if (standaloneError) {
    return (
      <div className='flex h-screen items-center justify-center'>
        Signed in, but failed to load studio. Please try logging in again.
      </div>
    );
  }

  if (userInvalid) {
    return (
      <div className='flex h-screen items-center justify-center'>
        Please open the correct Enrollio Studio menu link
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex h-screen items-center justify-center'>
        Something went wrong ....
      </div>
    );
  }

  if (isStandaloneDev || isFetching || !authHydrated) {
    return (
      <div className='flex h-screen items-center justify-center'>
        Loading....
      </div>
    );
  }

  return null;
};

const authSearchParamSchema = z.object({
  locationId: z.string(),
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/ghl-authorize')({
  validateSearch: zodValidator(authSearchParamSchema),
  component: Component,
});
